import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionDialog } from '@/components/hr/ActionDialog';
import { ErrorNote, Field, FormActions, FormGroup, FormPanel, OkNote, Select, errorMessage, money, shortDate, todayIso } from '@/components/ui';
import { useMarkSaved } from '@/components/unsavedForm';
import { hrGet, taxYearLabel, taxYearOf, useHrInvalidation } from '@/hooks/hr';
import { useAuth } from '@/stores/auth';

// ------------------------------------------------------------ payroll rules

type PerDayBasis = 'DAYS_30' | 'MONTH_DAYS' | 'DAYS_26';

interface HrSettings {
  perDayBasis: PerDayBasis;
  hoursPerDay: string;
  overtimeMultiplier: string;
  netRounding: number;
  dailyPaidHolidays: boolean;
  dailyPaidWeeklyOff: boolean;
  advanceLimitPercent: string;
  allowSecondLoan: boolean;
  leaveEncashment: boolean;
  gratuityEnabled: boolean;
  gratuityDaysPerYear: number;
  updatedAt?: string;
}

const BASIS: Record<PerDayBasis, { label: string; explain: string }> = {
  DAYS_30: {
    label: '30 days',
    explain: 'A day’s pay is always monthly pay ÷ 30, whatever the month. Rs 30,000 a month is Rs 1,000 a day.',
  },
  MONTH_DAYS: {
    label: 'Days in the month',
    explain: 'A day’s pay is monthly pay ÷ the days in that month (28 to 31), so a day in February is worth a little more.',
  },
  DAYS_26: {
    label: '26 working days',
    explain: 'A day’s pay is monthly pay ÷ 26, the usual count when one day a week is off. Rs 26,000 a month is Rs 1,000 a day.',
  },
};

function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="stack">
      <label className="settings__toggle">
        <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
        <span className="field__label">{label}</span>
      </label>
      {hint && <span className="settings__hint">{hint}</span>}
    </div>
  );
}

/** Payroll rules: one set for the whole company, saved on an explicit Save. */
export function PayrollRulesPage() {
  const { can } = useAuth();
  const editable = can('hr.setup.update');
  const rules = useQuery({
    queryKey: ['hr', 'setup', 'settings'],
    queryFn: () => hrGet<HrSettings>('/api/v1/hr/setup/settings'),
  });
  return (
    <div className="hrsetup__page">
      {rules.error && <ErrorNote message={rules.error.message} />}
      <FormPanel
        title="Payroll rules"
        icon={<AppIcon kind="setup" />}
        intro={
          editable
            ? 'These rules are the same for everyone. A change applies the next time a pay run is worked out; pay runs already posted do not change.'
            : 'These rules are the same for everyone. Only someone allowed to change HR Setup can change them.'
        }
      >
        {rules.isPending ? (
          <p className="muted">Loading…</p>
        ) : rules.data ? (
          <RulesForm key={rules.data.updatedAt ?? 'rules'} saved={rules.data} editable={editable} />
        ) : null}
      </FormPanel>
    </div>
  );
}

function RulesForm({ saved, editable }: { saved: HrSettings; editable: boolean }) {
  const invalidate = useHrInvalidation();
  const markSaved = useMarkSaved();
  const [v, setV] = useState({
    ...saved,
    hoursPerDay: String(Number(saved.hoursPerDay)),
    overtimeMultiplier: String(Number(saved.overtimeMultiplier)),
    advanceLimitPercent: String(Number(saved.advanceLimitPercent)),
    gratuityDaysPerYear: String(saved.gratuityDaysPerYear),
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const set = <K extends keyof typeof v>(key: K) => (value: (typeof v)[K]) => setV((s) => ({ ...s, [key]: value }));

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (v.perDayBasis !== saved.perDayBasis) body.perDayBasis = v.perDayBasis;
      if (Number(v.hoursPerDay) !== Number(saved.hoursPerDay)) body.hoursPerDay = v.hoursPerDay;
      if (Number(v.overtimeMultiplier) !== Number(saved.overtimeMultiplier)) body.overtimeMultiplier = v.overtimeMultiplier;
      if (v.netRounding !== saved.netRounding) body.netRounding = v.netRounding;
      for (const key of ['dailyPaidHolidays', 'dailyPaidWeeklyOff', 'allowSecondLoan', 'leaveEncashment', 'gratuityEnabled'] as const) {
        if (v[key] !== saved[key]) body[key] = v[key];
      }
      if (Number(v.advanceLimitPercent) !== Number(saved.advanceLimitPercent)) body.advanceLimitPercent = v.advanceLimitPercent;
      if (Number(v.gratuityDaysPerYear) !== saved.gratuityDaysPerYear) body.gratuityDaysPerYear = Number(v.gratuityDaysPerYear);
      return api.patch('/api/v1/hr/setup/settings', body);
    },
    onSuccess: () => {
      markSaved();
      invalidate();
      setOk('Payroll rules saved. They apply the next time a pay run is worked out.');
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  function submit() {
    setOk(null);
    const hours = Number(v.hoursPerDay);
    if (!(hours >= 1 && hours <= 24)) return setProblem('Hours in a day must be from 1 to 24.');
    const ot = Number(v.overtimeMultiplier);
    if (!(ot >= 1 && ot <= 5)) return setProblem('Overtime rate must be from 1 to 5 times.');
    const limit = Number(v.advanceLimitPercent);
    if (!(limit >= 1 && limit <= 1000)) return setProblem('Advance limit must be from 1% to 1000%.');
    const g = Number(v.gratuityDaysPerYear);
    if (!(Number.isInteger(g) && g >= 0 && g <= 365)) return setProblem('Gratuity days must be a whole number from 0 to 365.');
    setProblem(null);
    save.mutate();
  }

  const disabled = !editable || save.isPending;

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <FormGroup title="A day's pay" columns={2} hint={BASIS[v.perDayBasis]?.explain}>
        <Select
          label="Work out a day's pay from"
          required
          value={v.perDayBasis}
          onChange={(x) => set('perDayBasis')(x as PerDayBasis)}
          options={(Object.keys(BASIS) as PerDayBasis[]).map((b) => ({ value: b, label: BASIS[b].label }))}
          placeholder=""
          disabled={disabled}
        />
        <Field label="Hours in a working day" type="number" min={1} max={24} required value={v.hoursPerDay} onChange={set('hoursPerDay')} disabled={disabled} />
        <Field
          label="Overtime rate (×)"
          type="number"
          min={1}
          max={5}
          required
          value={v.overtimeMultiplier}
          onChange={set('overtimeMultiplier')}
          disabled={disabled}
          hint="2 means each overtime hour is paid double the normal hourly rate."
        />
        <Select
          label="Round net pay to the nearest"
          required
          value={String(v.netRounding)}
          onChange={(x) => set('netRounding')(Number(x))}
          options={[
            { value: '1', label: 'Rs 1' },
            { value: '10', label: 'Rs 10' },
            { value: '100', label: 'Rs 100' },
          ]}
          placeholder=""
          disabled={disabled}
        />
      </FormGroup>

      <FormGroup title="Daily-wage staff" columns={2}>
        <Toggle label="Pay them for holidays" checked={v.dailyPaidHolidays} onChange={set('dailyPaidHolidays')} disabled={disabled} />
        <Toggle label="Pay them for weekly offs" checked={v.dailyPaidWeeklyOff} onChange={set('dailyPaidWeeklyOff')} disabled={disabled} />
      </FormGroup>

      <FormGroup title="Advances and loans" columns={2}>
        <Field
          label="Advance limit (% of monthly pay)"
          type="number"
          min={1}
          max={1000}
          required
          value={v.advanceLimitPercent}
          onChange={set('advanceLimitPercent')}
          disabled={disabled}
          hint="100 means nobody can take more than one month's pay at a time."
        />
        <Toggle
          label="Allow a second loan while one is still owed"
          checked={v.allowSecondLoan}
          onChange={set('allowSecondLoan')}
          disabled={disabled}
        />
      </FormGroup>

      <FormGroup title="When someone leaves" columns={2}>
        <Toggle
          label="Pay out unused leave"
          hint="Only leave types marked “paid out on leaving”."
          checked={v.leaveEncashment}
          onChange={set('leaveEncashment')}
          disabled={disabled}
        />
        <Toggle label="Pay gratuity" checked={v.gratuityEnabled} onChange={set('gratuityEnabled')} disabled={disabled} />
        <Field
          label="Gratuity: days of pay for each full year worked"
          type="number"
          min={0}
          max={365}
          value={v.gratuityDaysPerYear}
          onChange={set('gratuityDaysPerYear')}
          disabled={disabled || !v.gratuityEnabled}
        />
      </FormGroup>

      {saved.updatedAt && <p className="muted settings__hint">Last changed {shortDate(saved.updatedAt)}.</p>}
      {problem && <ErrorNote message={problem} />}
      {ok && <OkNote message={ok} />}
      {editable && (
        <FormActions
          cancelLabel="Undo changes"
          onCancel={() => {
            setV({
              ...saved,
              hoursPerDay: String(Number(saved.hoursPerDay)),
              overtimeMultiplier: String(Number(saved.overtimeMultiplier)),
              advanceLimitPercent: String(Number(saved.advanceLimitPercent)),
              gratuityDaysPerYear: String(saved.gratuityDaysPerYear),
            });
            setProblem(null);
            markSaved();
          }}
        >
          <button type="submit" className="btn btn--primary" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save rules'}
          </button>
        </FormActions>
      )}
    </form>
  );
}

// ------------------------------------------------------------ tax years

interface Slab {
  id?: string;
  fromAmount: string | null;
  toAmount: string | null;
  fixedTax: string | null;
  ratePercent: string;
}

interface TaxYear {
  id: string;
  taxYear: number;
  label: string;
  incomeTaxEnabled: boolean;
  surchargeThreshold: string | null;
  surchargeRate: string | null;
  eobiEnabled: boolean;
  eobiWageBase: string | null;
  eobiEmployeeRate: string;
  eobiEmployerRate: string;
  pessiEnabled: boolean;
  pessiRate: string;
  pessiWageCeiling: string | null;
  minimumWage: string | null;
  slabs: Slab[];
  updatedAt: string;
}

/** "40000.00" → "40000", "1.0000" → "1", null → "". */
function plain(v: string | null | undefined): string {
  return v === null || v === undefined || v === '' ? '' : String(Number(v));
}

interface SlabDraft {
  key: number;
  fromAmount: string;
  toAmount: string;
  fixedTax: string;
  ratePercent: string;
}

function draftOf(y: TaxYear) {
  return {
    incomeTaxEnabled: y.incomeTaxEnabled,
    surchargeThreshold: plain(y.surchargeThreshold),
    surchargeRate: plain(y.surchargeRate),
    eobiEnabled: y.eobiEnabled,
    eobiWageBase: plain(y.eobiWageBase),
    eobiEmployeeRate: plain(y.eobiEmployeeRate),
    eobiEmployerRate: plain(y.eobiEmployerRate),
    pessiEnabled: y.pessiEnabled,
    pessiRate: plain(y.pessiRate),
    pessiWageCeiling: plain(y.pessiWageCeiling),
    minimumWage: plain(y.minimumWage),
    slabs: y.slabs.map((s, i) => ({
      key: i,
      fromAmount: plain(s.fromAmount),
      toAmount: plain(s.toAmount),
      fixedTax: plain(s.fixedTax),
      ratePercent: plain(s.ratePercent),
    })) as SlabDraft[],
  };
}

type Draft = ReturnType<typeof draftOf>;

const slabKey = (s: SlabDraft[]) => JSON.stringify(s.map(({ key: _key, ...rest }) => rest));

/**
 * Income tax, EOBI and PESSI for one tax year (July to June). Everything is
 * off until someone checks this year's rates and switches it on.
 */
export function TaxPage() {
  const { can } = useAuth();
  const invalidate = useHrInvalidation();
  const editable = can('hr.setup.update');
  const years = useQuery({
    queryKey: ['hr', 'setup', 'tax-years'],
    queryFn: () => hrGet<TaxYear[]>('/api/v1/hr/setup/tax-years'),
  });
  const list = years.data ?? [];
  const current = taxYearOf(todayIso());
  const [chosen, setChosen] = useState<string>('');
  const [adding, setAdding] = useState(false);
  const [ok, setOk] = useState<string | null>(null);
  const year = list.find((y) => String(y.taxYear) === chosen) ?? list.find((y) => y.taxYear === current) ?? list[0];

  const free = Array.from({ length: 5 }, (_, i) => current + 1 - i).filter((y) => !list.some((x) => x.taxYear === y));

  return (
    <div className="hrsetup__page">
      <FormPanel
        title="Tax and government deductions"
        icon={<AppIcon kind="tax" />}
        intro="Income tax, EOBI and PESSI for each tax year (July to June). Each one is switched off until you switch it on."
      >
        <div className="hrwarnbox hrwarnbox--alert" role="note">
          These rates are a starting point. Check them against this year's Finance Act and the EOBI/PESSI notifications
          before switching anything on.
        </div>
        <div className="filterbar" data-unsaved-ignore>
          <Select
            label="Tax year (July to June)"
            value={year ? String(year.taxYear) : ''}
            onChange={(x) => {
              setChosen(x);
              setOk(null);
            }}
            options={list.map((y) => ({ value: String(y.taxYear), label: taxYearLabel(y.taxYear) }))}
            prompt={list.length ? 'Choose…' : 'No tax years yet'}
          />
          {editable && (
            <button type="button" className="btn btn--withicon" onClick={() => setAdding(true)} disabled={free.length === 0}>
              <AppIcon kind="add" size={15} />
              Add a tax year
            </button>
          )}
        </div>
        {years.error && <ErrorNote message={years.error.message} />}
        {ok && <OkNote message={ok} />}
        {years.isPending ? (
          <p className="muted">Loading…</p>
        ) : !year ? (
          <p className="muted">No tax years are set up yet. Add one to set income tax, EOBI and PESSI.</p>
        ) : (
          <TaxYearForm
            key={`${year.id}-${year.updatedAt}`}
            year={year}
            editable={editable}
            onSaved={(message) => {
              invalidate();
              setOk(message);
            }}
          />
        )}
      </FormPanel>
      {adding && (
        <ActionDialog
          title="Add a tax year"
          icon="tax"
          intro="A new year starts with everything switched off. Copying last year brings its rates and tax table across for you to check."
          fields={[
            {
              key: 'taxYear',
              label: 'Tax year',
              type: 'select',
              required: true,
              options: free.map((y) => ({ value: String(y), label: taxYearLabel(y) })),
            },
            {
              key: 'copyFrom',
              label: 'Copy rates from',
              type: 'select',
              options: list.map((y) => ({ value: String(y.taxYear), label: taxYearLabel(y.taxYear) })),
            },
          ]}
          initial={{
            taxYear: free[0] !== undefined ? String(free[0]) : '',
            copyFrom: list[0] ? String(list[0].taxYear) : '',
          }}
          submitLabel="Add tax year"
          onClose={() => setAdding(false)}
          onSubmit={async (v) => {
            await api.post('/api/v1/hr/setup/tax-years', {
              taxYear: Number(v.taxYear),
              ...(v.copyFrom ? { copyFrom: Number(v.copyFrom) } : {}),
            });
            setChosen(v.taxYear ?? '');
            invalidate();
            setOk(`${taxYearLabel(Number(v.taxYear))} added. Everything in it is switched off.`);
          }}
        />
      )}
    </div>
  );
}

function TaxYearForm({ year, editable, onSaved }: { year: TaxYear; editable: boolean; onSaved: (message: string) => void }) {
  const markSaved = useMarkSaved();
  const saved = draftOf(year);
  const [v, setV] = useState<Draft>(saved);
  const [nextKey, setNextKey] = useState(saved.slabs.length);
  const [problem, setProblem] = useState<string | null>(null);
  const set = <K extends keyof Draft>(key: K) => (value: Draft[K]) => setV((s) => ({ ...s, [key]: value }));
  const disabled = !editable;

  function setSlab(key: number, field: keyof Omit<SlabDraft, 'key'>, value: string) {
    setV((s) => ({ ...s, slabs: s.slabs.map((r) => (r.key === key ? { ...r, [field]: value } : r)) }));
  }

  function addSlab() {
    setV((s) => {
      const last = s.slabs[s.slabs.length - 1];
      return {
        ...s,
        slabs: [...s.slabs, { key: nextKey, fromAmount: last?.toAmount || (last ? '' : '0'), toAmount: '', fixedTax: '0', ratePercent: '0' }],
      };
    });
    setNextKey((k) => k + 1);
  }

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      const text = ['surchargeThreshold', 'surchargeRate', 'pessiWageCeiling'] as const;
      for (const key of text) if (v[key] !== saved[key]) body[key] = v[key] || null;
      const required = ['eobiWageBase', 'eobiEmployeeRate', 'eobiEmployerRate', 'pessiRate', 'minimumWage'] as const;
      for (const key of required) if (v[key] !== saved[key]) body[key] = v[key] || '0';
      for (const key of ['incomeTaxEnabled', 'eobiEnabled', 'pessiEnabled'] as const) if (v[key] !== saved[key]) body[key] = v[key];
      if (slabKey(v.slabs) !== slabKey(saved.slabs)) {
        body.slabs = v.slabs.map((s) => ({
          fromAmount: s.fromAmount || '0',
          toAmount: s.toAmount || null,
          fixedTax: s.fixedTax || '0',
          ratePercent: s.ratePercent || '0',
        }));
      }
      return api.patch(`/api/v1/hr/setup/tax-years/${year.id}`, body);
    },
    onSuccess: () => {
      markSaved();
      onSaved(`${taxYearLabel(year.taxYear)} saved.`);
    },
    onError: (e) => setProblem(errorMessage(e, 'Could not save')),
  });

  const changed =
    JSON.stringify({ ...v, slabs: slabKey(v.slabs) }) !== JSON.stringify({ ...saved, slabs: slabKey(saved.slabs) });

  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        setProblem(null);
        save.mutate();
      }}
    >
      <FormGroup card title={`Income tax — ${taxYearLabel(year.taxYear)}`} hint="Worked out on each person's taxable pay for the whole year, using the table below.">
        <label className="settings__toggle hrsetup__switch">
          <input type="checkbox" checked={v.incomeTaxEnabled} disabled={disabled} onChange={(e) => set('incomeTaxEnabled')(e.target.checked)} />
          <span>Take income tax from pay: {v.incomeTaxEnabled ? 'On' : 'Off'}</span>
        </label>
        <div className="tablewrap">
          <table className="table hrslabs">
            <thead>
              <tr>
                <th>Yearly taxable pay from</th>
                <th>Up to</th>
                <th>Fixed tax</th>
                <th>% on the amount above “from”</th>
                {editable && <th aria-label="Remove" />}
              </tr>
            </thead>
            <tbody>
              {v.slabs.length === 0 && (
                <tr>
                  <td colSpan={editable ? 5 : 4} className="muted">
                    No tax table yet. Add the bands before switching income tax on.
                  </td>
                </tr>
              )}
              {v.slabs.map((s, i) => (
                <tr key={s.key}>
                  <td>
                    <input className="field__input" type="number" min={0} aria-label={`Band ${i + 1} from`} value={s.fromAmount} disabled={disabled} onChange={(e) => setSlab(s.key, 'fromAmount', e.target.value)} />
                  </td>
                  <td>
                    <input className="field__input" type="number" min={0} aria-label={`Band ${i + 1} up to`} placeholder="No limit" value={s.toAmount} disabled={disabled} onChange={(e) => setSlab(s.key, 'toAmount', e.target.value)} />
                  </td>
                  <td>
                    <input className="field__input" type="number" min={0} aria-label={`Band ${i + 1} fixed tax`} value={s.fixedTax} disabled={disabled} onChange={(e) => setSlab(s.key, 'fixedTax', e.target.value)} />
                  </td>
                  <td>
                    <input className="field__input" type="number" min={0} max={100} step="any" aria-label={`Band ${i + 1} rate`} value={s.ratePercent} disabled={disabled} onChange={(e) => setSlab(s.key, 'ratePercent', e.target.value)} />
                  </td>
                  {editable && (
                    <td>
                      <button
                        type="button"
                        className="btn btn--sm btn--ghost btn--withicon"
                        onClick={() => setV((x) => ({ ...x, slabs: x.slabs.filter((r) => r.key !== s.key) }))}
                      >
                        <AppIcon kind="remove" size={14} />
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {editable && (
          <div className="toolbar">
            <button type="button" className="btn btn--sm btn--withicon" onClick={addSlab}>
              <AppIcon kind="add" size={14} />
              Add a band
            </button>
            <span className="muted">The first band starts at 0, each ends where the next begins, and the last has no limit.</span>
          </div>
        )}
        <div className="formgrid formgrid--2">
          <Field
            label="Surcharge above yearly pay of"
            type="number"
            min={0}
            value={v.surchargeThreshold}
            onChange={set('surchargeThreshold')}
            disabled={disabled}
            hint="Leave empty for no surcharge."
          />
          <Field label="Surcharge (% of the tax)" type="number" min={0} max={100} value={v.surchargeRate} onChange={set('surchargeRate')} disabled={disabled} />
        </div>
      </FormGroup>

      <FormGroup card title="EOBI" hint="Only for staff marked “EOBI registered”. Worked out on the wage base, not on actual pay.">
        <label className="settings__toggle hrsetup__switch">
          <input type="checkbox" checked={v.eobiEnabled} disabled={disabled} onChange={(e) => set('eobiEnabled')(e.target.checked)} />
          <span>Take EOBI: {v.eobiEnabled ? 'On' : 'Off'}</span>
        </label>
        <div className="formgrid formgrid--3">
          <Field label="Wage base (Rs a month)" type="number" min={0} value={v.eobiWageBase} onChange={set('eobiWageBase')} disabled={disabled} />
          <Field label="Employee share (%)" type="number" min={0} max={100} value={v.eobiEmployeeRate} onChange={set('eobiEmployeeRate')} disabled={disabled} hint="Taken from pay." />
          <Field label="Company share (%)" type="number" min={0} max={100} value={v.eobiEmployerRate} onChange={set('eobiEmployerRate')} disabled={disabled} hint="Paid by the company on top." />
        </div>
      </FormGroup>

      <FormGroup card title="PESSI" hint="Only for staff marked “PESSI registered”. Paid by the company; never taken from pay.">
        <label className="settings__toggle hrsetup__switch">
          <input type="checkbox" checked={v.pessiEnabled} disabled={disabled} onChange={(e) => set('pessiEnabled')(e.target.checked)} />
          <span>Pay PESSI: {v.pessiEnabled ? 'On' : 'Off'}</span>
        </label>
        <div className="formgrid formgrid--2">
          <Field label="Rate (%)" type="number" min={0} max={100} value={v.pessiRate} onChange={set('pessiRate')} disabled={disabled} />
          <Field
            label="Only for pay up to (Rs a month)"
            type="number"
            min={0}
            value={v.pessiWageCeiling}
            onChange={set('pessiWageCeiling')}
            disabled={disabled}
            hint="Leave empty for no limit."
          />
        </div>
      </FormGroup>

      <FormGroup card title="Minimum wage" hint="Used only to warn when someone is paid less. It never stops a pay run.">
        <div className="formgrid formgrid--2">
          <Field label="Minimum wage (Rs a month)" type="number" min={0} value={v.minimumWage} onChange={set('minimumWage')} disabled={disabled} hint={v.minimumWage ? `Now ${money(v.minimumWage)} a month.` : undefined} />
        </div>
      </FormGroup>

      <p className="muted settings__hint">Last changed {shortDate(year.updatedAt)}.</p>
      {problem && <ErrorNote message={problem} />}
      {editable && (
        <FormActions
          cancelLabel="Undo changes"
          onCancel={() => {
            setV(saved);
            setProblem(null);
            markSaved();
          }}
        >
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !changed}>
            {save.isPending ? 'Saving…' : `Save ${taxYearLabel(year.taxYear)}`}
          </button>
        </FormActions>
      )}
    </form>
  );
}
